import React, { useEffect, useRef, useState } from 'react';
import { Box, Fade, FormGroup } from '@material-ui/core';
import { authenticationUrl } from 'http/endpoints';
import { emailIsValidRe, setCookie } from 'globals/utils';
import axios from 'axios';
import { loginInitialStep as loginLocales } from 'globals/locales/registration';
import defaultLocales from 'globals/locales/default';
import FsSimpleInput from 'ui-elements/form-elements/FsDefaultInput';
import FsFormFullWidthContainer from 'ui-elements/form-elements/FsFormFullWidthContainer';
import FsPasswordInput from 'ui-elements/form-elements/FsPasswordInput';
import FsPendingButton from 'ui-elements/buttons/FsPendingButton';
import FsNextArrowIcon from 'ui-elements/icons/FsNextArrow';
import FsAlert from 'ui-elements/alerts/FsAlert';

interface FsLoginFormProps {
  onSuccess?: () => void;
}

const FsLoginForm: React.FC<FsLoginFormProps> = ({ onSuccess }) => {
  const formEl = useRef<HTMLFormElement>(null);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showAlerts, setShowAlerts] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const verify = async (): Promise<void> => {
    setPending(true);
    const url = authenticationUrl;
    const payload = {
      email,
      password,
      returnSecureToken: true,
    };
    const req = await axios
      .post<{ idToken: string }>(url, payload)
      .catch((e) => {
        setShowAlerts(true);
        // eslint-disable-next-line no-console
        console.warn(e);
      });
    if (req && req.status === 200) {
      const { idToken } = req.data;
      setCookie('fs-token', idToken, 1);
      setPending(false);
      if (onSuccess) onSuccess();
      return;
    }
    setShowAlerts(true);
    setPending(false);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    await verify();
  };

  useEffect(() => {
    const isDefaultFormValid = !!formEl?.current?.checkValidity();
    const isEmailValid = !!email.match(emailIsValidRe);
    const isPasswordValid = password.length >= 3;
    setIsFormValid(isDefaultFormValid && isEmailValid && isPasswordValid);
  }, [email, password]);

  return (
    <form onSubmit={handleSubmit} ref={formEl}>
      <FormGroup>
        {showAlerts && (
          <Fade in timeout={300}>
            <Box mb={1.2}>
              <FsAlert severity="error">
                {loginLocales.notifications.errorWrongCredentials}
              </FsAlert>
            </Box>
          </Fade>
        )}

        <FsFormFullWidthContainer>
          <FsSimpleInput
            fsVariant="outlined"
            name={defaultLocales.email.toLowerCase()}
            label={defaultLocales.email}
            value={email}
            onChange={(e): void => {
              setEmail(e.target.value);
              setShowAlerts(false);
            }}
            fullWidth
            variant="outlined"
            autoFocus
          />
        </FsFormFullWidthContainer>

        <FsFormFullWidthContainer>
          <FsPasswordInput
            fsVariant="outlined"
            name={defaultLocales.password.toLowerCase()}
            label={defaultLocales.password}
            type="password"
            value={password}
            onChange={(e): void => {
              setPassword(e.target.value);
              setShowAlerts(false);
            }}
            fullWidth
            variant="outlined"
          />
        </FsFormFullWidthContainer>

        <Box>
          <FsPendingButton
            type="submit"
            variant="contained"
            color="primary"
            pending={pending}
            disabled={!isFormValid}
            endIcon={<FsNextArrowIcon />}
            width={160}
            spaceBetween
          >
            {defaultLocales.logIn}
          </FsPendingButton>
        </Box>
      </FormGroup>
    </form>
  );
};

export default FsLoginForm;
